import React, { useEffect, useState } from "react";
import { Service } from "../../interfaces/service";
import {
  createService,
  deleteService,
  getAllAllServices,
  updateService,
} from "../../services/serviceService";
import { getVendors } from "../../services/vendorService";
import { Vendor } from "../../interfaces/vendor";
import { getOrderFormTypes } from "../../services/orderFormService";
import { OrderFormType } from "../../interfaces/orderFormType";
import ToolTip from "../../components/component-tools/ToolTip";
import { EmailTemplate } from "../../interfaces/emailTemplate";
import {
  getEmailTemplates,
  updateEmailTemplate,
} from "../../services/emailTemplateService";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { sortServices } from "../../services/helpers";

const EditDefaults = () => {
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [orderFormTypes, setOrderFormTypes] = useState<OrderFormType[] | null>(
    null,
  );
  const [isNewService, setIsNewService] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [selectedEmailTemplate, setSelectedEmailTemplate] =
    useState<EmailTemplate | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingSpinner(true);
        const servicesResponse = await getAllAllServices();
        const sortedServices = await sortServices(servicesResponse);
        //filter out global security check serviceId 35
        const filteredServices = sortedServices.filter(
          (service) => service.serviceID !== 35,
        );
        setServices(filteredServices);
        if (filteredServices.length > 0) {
          setSelectedService(filteredServices[0]);
        }
        const vendorsResponse = await getVendors();
        const sortedVendors = vendorsResponse.filter(
          (vendor) => vendor.isActive,
        );
        setVendors(sortedVendors);
        const orderFormTypesResponse = await getOrderFormTypes();
        setOrderFormTypes(orderFormTypesResponse);
        const emailTemplatesResponse = await getEmailTemplates();
        setEmailTemplates(emailTemplatesResponse);
        if (emailTemplatesResponse.length > 0) {
          setSelectedEmailTemplate(emailTemplatesResponse[0]);
        }
      } catch (error) {
        console.error("Error fetching bishops:", error);
      } finally {
        setLoadingSpinner(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const handleSaveService = async () => {
    if (!selectedService) return;

    try {
      setLoadingSpinner(true);

      if (isNewService) {
        await createService(selectedService);
        setSuccessMessage("Service created successfully.");

        const servicesResponse = await getAllAllServices();
        setServices(servicesResponse);
        setSelectedService(servicesResponse[0]);
        setIsNewService(false);
      } else {
        await updateService(selectedService);
        setSuccessMessage("Service updated successfully.");
      }
    } catch (error) {
      console.error("Error saving service:", error);
      setErrorMessage(
        isNewService
          ? "Failed to create service."
          : "Failed to update service.",
      );
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleSaveEmailTemplate = async () => {
    if (!selectedEmailTemplate) return;

    try {
      setLoadingSpinner(true);

      await updateEmailTemplate(selectedEmailTemplate);

      setEmailTemplates((prevTemplates) =>
        prevTemplates.map((template) =>
          template.templateID === selectedEmailTemplate.templateID
            ? selectedEmailTemplate
            : template,
        ),
      );

      setSuccessMessage("Email template updated successfully.");
    } catch (error) {
      console.error("Error saving service:", error);
      setErrorMessage("Failed to update email template");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleDeleteService = async () => {
    if (!selectedService) return;

    try {
      setLoadingSpinner(true);
      await deleteService(selectedService.serviceID);
      setSuccessMessage("Service deleted successfully.");
      const servicesResponse = await getAllAllServices();
      setServices(servicesResponse);
      setSelectedService(servicesResponse[0]);
    } catch (error) {
      console.error("Error saving service:", error);
      setErrorMessage(
        "There are already orders using this serviceID. Cannot delete.",
      );
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleNewService = () => {
    setSelectedService(null);
    setIsEditing(true);
    setIsNewService(true);
    setSelectedService({
      serviceID: 0,
      serviceName: "",
      fee: 20,
      isActive: true,
      orderFormTypeID: 1,
      referenceYears: 0,
      vendorID: 1,
      clientVisible: true,
      fee2: 0,
      subscriptionService: false,
    });
  };

  const handleCancel = () => {
    if (services.length > 0) {
      setSelectedService(services[0]);
    } else {
      setSelectedService(null);
    }
    setIsNewService(false);
    setSuccessMessage("");
    setErrorMessage(null);
    setIsEditing(false);
  };

  const handleFieldChange = (
    field: keyof Service,
    value: string | number | boolean,
  ) => {
    setIsEditing(true);
    if (!selectedService) return;
    setSelectedService({ ...selectedService, [field]: value });
  };

  const handleEmailFieldChange = (
    field: keyof EmailTemplate,
    value: string | number | boolean,
  ) => {
    setIsEditingEmail(true);
    if (!selectedEmailTemplate) return;
    setSelectedEmailTemplate({ ...selectedEmailTemplate, [field]: value });
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <div className="page-items-container" style={{ alignItems: "center" }}>
          <h1>Edit Defaults</h1>
        </div>
      </div>
      <div className="divider"></div>

      {/* Edit Service */}
      <div className="data-container" style={{ width: "100%" }}>
        <div className="space-between-container">
          <div className="field-long" style={{ gap: "40px", width: "80%" }}>
            <div className="text-sub-header">Service to Update</div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "10px",
                width: "40%",
              }}
            >
              <select
                className="form-control"
                value={
                  isNewService
                    ? "NEW SERVICE"
                    : selectedService?.serviceID || ""
                }
                onChange={(e) => {
                  const selected = services.find(
                    (s) => s.serviceID === parseInt(e.target.value),
                  );
                  setSelectedService(selected || null);
                }}
                disabled={isNewService}
              >
                {!isNewService &&
                  services.map((service) => (
                    <option key={service.serviceID} value={service.serviceID}>
                      {service.serviceName}
                    </option>
                  ))}
                {isNewService && (
                  <option value="NEW SERVICE">NEW SERVICE</option>
                )}
              </select>
              <ToolTip
                tooltipText="To remove a service from use, set 'Active' to 'No'. To remove a service from client view, set 'Client Visible' to 'No'."
                placement="top"
                className="info-icon-spec"
              ></ToolTip>
            </div>
          </div>

          <button className="oxbtn btn-new" onClick={handleNewService}>
            <div> New Service </div>
            <i className="bi bi-plus-circle-fill plus-icon"></i>
          </button>
        </div>

        <div
          className="divider-small"
          style={{ width: "60%", margin: "0px" }}
        ></div>
        <div style={{ flex: "0 0 30%" }}> </div>

        <div className="field-stacked" style={{ flex: "0 0 25%" }}>
          <label className="field-label">Service Name:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={selectedService?.serviceName || ""}
              onChange={(e) => handleFieldChange("serviceName", e.target.value)}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 5%" }}>
          <label className="field-label">Fee:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={selectedService?.fee ?? 0}
              onChange={(e) =>
                handleFieldChange("fee", parseFloat(e.target.value))
              }
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 7%" }}>
          <label className="field-label">Active:</label>
          <div className="field-input">
            <select
              className="form-control"
              value={selectedService?.isActive ? "true" : "false"}
              onChange={(e) =>
                handleFieldChange("isActive", e.target.value === "true")
              }
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 7%" }}>
          <label className="field-label">Client Visible:</label>
          <div className="field-input">
            <select
              className="form-control"
              value={selectedService?.clientVisible ? "true" : "false"}
              onChange={(e) =>
                handleFieldChange("clientVisible", e.target.value === "true")
              }
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
        </div>
        {orderFormTypes && (
          <div className="field-stacked" style={{ flex: "0 0 20%" }}>
            <label className="field-label">Order Form Type:</label>
            <div className="field-input">
              <select
                className="form-control"
                value={selectedService?.orderFormTypeID ?? ""}
                onChange={(e) =>
                  handleFieldChange("orderFormTypeID", parseInt(e.target.value))
                }
              >
                {orderFormTypes.map((type) => (
                  <option
                    key={type.orderFormTypeID}
                    value={type.orderFormTypeID}
                  >
                    {type.description}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="field-stacked" style={{ flex: "0 0 25%" }}>
          <label className="field-label">Vendor:</label>
          <div className="field-input">
            <select
              className="form-control"
              value={selectedService?.vendorID ?? ""}
              onChange={(e) =>
                handleFieldChange("vendorID", parseInt(e.target.value))
              }
            >
              {vendors.map((vendor) => (
                <option key={vendor.vendorID} value={vendor.vendorID}>
                  {vendor.vendorName}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="action-buttons">
          <button
            className={`smbtn btn-green  ${!isEditing ? "disabled" : ""}`}
            onClick={handleSaveService}
            disabled={!isEditing}
          >
            {isNewService ? "Create" : "Save"}
          </button>
          {isNewService && (
            <button
              className={`smbtn btn-grey}
                    }`}
              onClick={handleCancel}
            >
              Cancel
            </button>
          )}
          {!isNewService && (
            <button className={`smbtn btn-red`} onClick={handleDeleteService}>
              Delete
            </button>
          )}
        </div>
      </div>

      {/* <div className="divider-small"></div> */}

      {/* Edit Email Template */}
      <div className="data-container" style={{ width: "100%" }}>
        <div className="space-between-container">
          <div className="field-long" style={{ gap: "40px", width: "80%" }}>
            <div className="text-sub-header">Email Template to Update</div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "10px",
                width: "40%",
              }}
            >
              <select
                className="form-control"
                value={selectedEmailTemplate?.templateID || ""}
                onChange={(e) => {
                  const selected = emailTemplates.find(
                    (s) => s.templateID === parseInt(e.target.value),
                  );
                  setSelectedEmailTemplate(selected || null);
                }}
              >
                {emailTemplates.map((template) => (
                  <option key={template.templateID} value={template.templateID}>
                    {template.name}
                  </option>
                ))}
              </select>
              <ToolTip
                tooltipText="Emails sent by system. Do not paragraph breaks in editor. Line breaks include spacing by default."
                placement="top"
                className="info-icon-spec"
              ></ToolTip>
            </div>
          </div>
        </div>
        <div
          className="divider-small"
          style={{ width: "60%", margin: "0px" }}
        ></div>
        <div style={{ flex: "0 0 30%" }}> </div>
        <div className="field-stacked" style={{ flex: "0 0 50%" }}>
          <label className="field-label">Subject:</label>
          <div className="field-input">
            <input
              key={selectedEmailTemplate?.templateID}
              className="form-control"
              type="text"
              value={selectedEmailTemplate?.subject ?? ""}
              onChange={(e) =>
                handleEmailFieldChange("subject", e.target.value)
              }
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 100%" }}>
          <label className="field-label">Body:</label>
          <div className="field-input">
            <ReactQuill
              key={selectedEmailTemplate?.templateID}
              theme="snow"
              value={selectedEmailTemplate?.bodyHtml ?? ""}
              onChange={(content) =>
                handleEmailFieldChange("bodyHtml", content)
              }
              style={{ height: "300px", marginBottom: "50px" }}
            />
          </div>
        </div>

        <div className="action-buttons">
          <button
            className={`smbtn btn-green  ${!isEditingEmail ? "disabled" : ""}`}
            onClick={handleSaveEmailTemplate}
            disabled={!isEditingEmail}
          >
            {"Save"}
          </button>
        </div>
      </div>

      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
      {successMessage && (
        <div className="alert-message alert alert-success alert-dismissible fade show">
          {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
      {errorMessage && (
        <div
          className="alert-message alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
    </div>
  );
};

export default EditDefaults;
